/**
 * Search Utilities for Quick User Search
 * Provides efficient search functionality with role-based filtering
 */

/**
 * Normalize string for search comparison
 * @param {string} str - String to normalize
 * @returns {string} - Normalized string
 */
export const normalizeString = (str) => {
  if (!str) return '';
  return str.toString().toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Normalize phone number for search
 * @param {string} phone - Phone number to normalize
 * @returns {string} - Normalized phone number
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters except +
  return phone.toString().replace(/[^\d+]/g, '');
};

/**
 * Check if user matches search query
 * @param {Object} user - User object
 * @param {string} query - Search query
 * @returns {boolean} - True if user matches query
 */
export const userMatchesQuery = (user, query) => {
  if (!query || !user) return false;
  
  const normalizedQuery = normalizeString(query);
  const normalizedPhoneQuery = normalizePhone(query);
  
  // Check first name
  if (user.firstName && normalizeString(user.firstName).includes(normalizedQuery)) {
    return true;
  }
  
  // Check last name
  if (user.lastName && normalizeString(user.lastName).includes(normalizedQuery)) {
    return true;
  }
  
  // Check full name
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`;
  if (normalizeString(fullName).includes(normalizedQuery)) {
    return true;
  }
  
  // Check mobile number
  if (user.mobile && normalizePhone(user.mobile).includes(normalizedPhoneQuery)) {
    return true;
  }
  
  // Check email (optional)
  if (user.email && normalizeString(user.email).includes(normalizedQuery)) {
    return true;
  }
  
  return false;
};

/**
 * Get user display name
 * @param {Object} user - User object
 * @returns {string} - Display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return '';
  
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  return fullName || user.email || user.mobile || 'Unknown User';
};

/**
 * Get user type label
 * @param {string} usertype - User type
 * @returns {string} - User type label
 */
export const getUserTypeLabel = (usertype) => {
  const typeLabels = {
    driver: 'Driver',
    customer: 'Customer',
    rider: 'Customer',
    admin: 'Admin',
    fleetadmin: 'Fleet Admin',
    finance: 'Finance',
    callcenteragent: 'Call Center Agent',
    callcentersupervisor: 'Call Center Supervisor'
  };
  
  return typeLabels[usertype] || usertype;
};

/**
 * Filter users based on role permissions
 * @param {Array} users - Array of users
 * @param {string} currentUserRole - Current user's role
 * @returns {Array} - Filtered users
 */
export const filterUsersByPermission = (users, currentUserRole) => {
  if (!users || !Array.isArray(users)) return [];
  
  // Define what user types each role can search
  const searchPermissions = {
    admin: ['driver', 'customer', 'rider', 'fleetadmin', 'finance', 'callcenteragent', 'callcentersupervisor'],
    fleetadmin: ['driver', 'customer', 'rider'],
    finance: ['driver', 'customer', 'rider'],
    callcenteragent: ['driver', 'customer', 'rider'],
    callcentersupervisor: ['driver', 'customer', 'rider', 'callcenteragent'],
    driver: [], // Drivers can't search users
    customer: [], // Customers can't search users
    rider: [] // Riders can't search users
  };
  
  const allowedTypes = searchPermissions[currentUserRole] || [];
  
  if (allowedTypes.length === 0) {
    return [];
  }
  
  return users.filter(user => 
    allowedTypes.includes(user.usertype) || 
    (user.usertype === 'rider' && allowedTypes.includes('customer')) // Treat riders as customers
  );
};

/**
 * Search users with query
 * @param {Array} users - Array of users
 * @param {string} query - Search query
 * @param {string} currentUserRole - Current user's role
 * @param {number} limit - Maximum number of results
 * @returns {Array} - Search results
 */
export const searchUsers = (users, query, currentUserRole, limit = 10) => {
  if (!query || query.length < 2) return [];
  
  // Filter users by permission
  const allowedUsers = filterUsersByPermission(users, currentUserRole);
  
  // Search through allowed users
  const results = allowedUsers.filter(user => userMatchesQuery(user, query));
  
  // Sort results by relevance (exact matches first)
  const sortedResults = results.sort((a, b) => {
    const aName = getUserDisplayName(a).toLowerCase();
    const bName = getUserDisplayName(b).toLowerCase();
    const queryLower = query.toLowerCase();
    
    // Exact match gets priority
    if (aName === queryLower) return -1;
    if (bName === queryLower) return 1;
    
    // Starts with query gets next priority
    if (aName.startsWith(queryLower) && !bName.startsWith(queryLower)) return -1;
    if (bName.startsWith(queryLower) && !aName.startsWith(queryLower)) return 1;
    
    // Otherwise alphabetical
    return aName.localeCompare(bName);
  });
  
  // Limit results
  return sortedResults.slice(0, limit);
};

/**
 * Group search results by user type
 * @param {Array} results - Search results
 * @returns {Object} - Grouped results
 */
export const groupResultsByType = (results) => {
  const grouped = {
    drivers: [],
    customers: [],
    others: []
  };
  
  results.forEach(user => {
    if (user.usertype === 'driver') {
      grouped.drivers.push(user);
    } else if (user.usertype === 'customer' || user.usertype === 'rider') {
      grouped.customers.push(user);
    } else {
      grouped.others.push(user);
    }
  });
  
  return grouped;
};

/**
 * Get user navigation URL based on user type
 * @param {Object} user - User object
 * @returns {string} - Navigation URL
 */
export const getUserNavigationUrl = (user) => {
  if (!user || !user.id) return '#';
  
  // Use the proper detail routes as defined in App.js
  switch (user.usertype) {
    case 'driver':
      return `/users/driverdetails/${user.id}`; // Driver details route
    case 'customer':
    case 'rider':
      return `/users/customerdetails/${user.id}`; // Customer details route
    case 'fleetadmin':
    case 'callcenteragent':
    case 'callcentersupervisor':
    case 'finance':
    case 'admin':
      // For internal staff, use the edit user route to view their details
      return `/users/edituser/${user.usertype}/${user.id}`;
    default:
      return `/users/customerdetails/${user.id}`;
  }
};

/**
 * Create search index for faster searching
 * @param {Array} users - Array of users
 * @returns {Map} - Search index
 */
export const createSearchIndex = (users) => {
  const index = new Map();
  
  if (!users || !Array.isArray(users)) return index;
  
  users.forEach(user => {
    // Index by first name
    if (user.firstName) {
      const key = normalizeString(user.firstName).charAt(0);
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(user);
    }
    
    // Index by last name
    if (user.lastName) {
      const key = normalizeString(user.lastName).charAt(0);
      if (!index.has(key)) index.set(key, []);
      if (!index.get(key).includes(user)) {
        index.get(key).push(user);
      }
    }
    
    // Index by phone number
    if (user.mobile) {
      const normalizedPhone = normalizePhone(user.mobile);
      if (normalizedPhone.length > 3) {
        const key = normalizedPhone.substring(0, 3);
        if (!index.has(key)) index.set(key, []);
        if (!index.get(key).includes(user)) {
          index.get(key).push(user);
        }
      }
    }
  });
  
  return index;
};

/**
 * Search using index for better performance
 * @param {Map} index - Search index
 * @param {string} query - Search query
 * @param {string} currentUserRole - Current user's role
 * @param {number} limit - Maximum number of results
 * @returns {Array} - Search results
 */
export const searchWithIndex = (index, query, currentUserRole, limit = 10) => {
  if (!query || query.length < 2 || !index) return [];
  
  const normalizedQuery = normalizeString(query);
  const phoneQuery = normalizePhone(query);
  
  // Get potential matches from index
  const potentialMatches = new Set();
  
  // Check first character index
  if (normalizedQuery.length > 0) {
    const firstChar = normalizedQuery.charAt(0);
    const indexedUsers = index.get(firstChar) || [];
    indexedUsers.forEach(user => potentialMatches.add(user));
  }
  
  // Check phone index
  if (phoneQuery.length >= 3) {
    const phonePrefix = phoneQuery.substring(0, 3);
    const phoneUsers = index.get(phonePrefix) || [];
    phoneUsers.forEach(user => potentialMatches.add(user));
  }
  
  // Convert to array and filter by permission
  const users = Array.from(potentialMatches);
  const allowedUsers = filterUsersByPermission(users, currentUserRole);
  
  // Perform actual search
  const results = allowedUsers.filter(user => userMatchesQuery(user, query));
  
  // Sort and limit
  return results.slice(0, limit);
};
